import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { Reveal } from './Reveal';
import { Button } from './Button';
import { PaymentCalculator } from './Calculator/PaymentCalculator';
import { trackVehicleInterest } from '../tracking';
import { scrollToLeadForm } from '../utils/scroll';
import { formatThousands, sanitizeVin } from '../utils/format';

/**
 * Second entry point into the SAME lead flow, for a different intent.
 *
 * The VIN is optional. It decodes to year, trim, and equipment, which is what makes an
 * appraisal accurate — it does NOT retrieve a payoff, since lenders release that only to
 * the account holder. A lead is never rejected over a missing or partial VIN; when it is
 * blank, three plain fallback fields appear instead.
 *
 * It never displays, estimates, or implies a dollar value for the trade — no numbers.
 */
const EMPTY = {
  vin: '',
  year: '',
  make: '',
  model: '',
  miles: '',
  payoff: '',
  payoffUnknown: false
};
export function TradeIn() {
  const {
    t
  } = useLanguage();
  const {
    setPresetIntent,
    setTradeIn
  } = useLead();
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState('');
  const vinIncomplete = values.vin.length > 0 && values.vin.length < 17;
  const showFallback = values.vin.length === 0;
  const set = <K extends keyof typeof EMPTY,>(key: K, value: (typeof EMPTY)[K]) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setError('');
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // Mileage is the only hard requirement. The VIN is never a blocker.
    if (!values.miles.trim()) {
      setError(t('tradein.err'));
      return;
    }
    setError('');
    setTradeIn({
      vin: values.vin.trim(),
      year: values.year.trim(),
      make: values.make.trim(),
      model: values.model.trim(),
      miles: values.miles.trim(),
      payoff: values.payoffUnknown ? '' : values.payoff.trim(),
      payoffUnknown: values.payoffUnknown
    });
    setPresetIntent('trade-in-buy');
    trackVehicleInterest('trade-in');
    scrollToLeadForm();
  };
  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-muted';
  const helpClass = 'mb-2 text-xs leading-relaxed text-muted';
  const inputClass = 'min-h-[52px] w-full border border-ink/20 bg-white px-4 text-base text-body placeholder:text-muted/60 focus:border-ink focus:outline-none';
  return <section className="w-full bg-offwhite" data-id="element-423" data-mp="2zlqz9xw7t1ru">
      <div className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 md:py-20" data-id="element-424" data-mp="2zkfvhy2rru0q">
        <Reveal data-id="element-425" data-mp="2lkem1u8fve9n">
          <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.04] tracking-[-0.02em] text-ink sm:text-4xl" data-id="element-426" data-mp="2lky4rd5wy6oq">
            {t('tradein.title')}
          </h2>
          <div aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-427" data-mp="1a59by55zadg0" />
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-body" data-id="element-428" data-mp="1f5x2yqu7iua8">{t('tradein.sub')}</p>
        </Reveal>

        <Reveal delay={0.1} data-id="element-429" data-mp="16nlosnvwc5dt">
          <form onSubmit={handleSubmit} noValidate className="mt-8" data-id="element-430" data-mp="16mshnlt0zdv5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-431" data-mp="12njoai1wdi04">
              <div className="sm:col-span-2" data-id="element-432" data-mp="1gmjcxppetwwa">
                <label htmlFor="tradeVin" className={labelClass} data-id="element-433" data-mp="1gl89jqzvmr5p">
                  {t('tradein.vin')}
                </label>
                <p id="tradeVin-help" className={helpClass} data-id="element-434" data-mp="1bldercczdhfd">
                  {t('tradein.vinHelp')}
                </p>
                <div className="relative" data-id="element-435" data-mp="iysm88krqza8">
                  <input id="tradeVin" name="tradeVin" type="text" autoComplete="off" spellCheck={false} placeholder={t('tradein.vinPlaceholder')} value={values.vin}
                // I, O and Q are rejected as you type — real VINs never contain them.
                onChange={e => set('vin', sanitizeVin(e.target.value))} aria-describedby={`tradeVin-help${vinIncomplete ? ' tradeVin-warn' : ''}`} className={`${inputClass} pr-20 font-mono tracking-[0.12em] placeholder:font-sans placeholder:tracking-normal`} data-id="element-436" data-mp="j03rkctv4f3g" />
                  <span aria-hidden="true" className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold tabular-nums ${values.vin.length === 17 ? 'text-camelink' : 'text-muted/70'}`} data-id="element-437" data-mp="38lp59fb9x4tj">
                    {t('tradein.vinCount', {
                    n: String(values.vin.length)
                  })}
                  </span>
                </div>
                {/* A short VIN is a hint, never an error — it must not block the lead. */}
                <p id="tradeVin-warn" className={`mt-1.5 text-xs ${vinIncomplete ? 'text-camelink' : 'text-muted'}`} data-id="element-438" data-mp="1xx82d4appvk2">
                  {vinIncomplete ? t('tradein.vinWarn') : t('tradein.vinOptional')}
                </p>
              </div>

              {showFallback && <div className="sm:col-span-2" data-id="element-439" data-mp="1pn9a85r19big">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-camelink" data-id="element-440" data-mp="1pmhf0w1clfsp">
                    {t('tradein.noVin')}
                  </p>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-id="element-441" data-mp="1gp745bsfzsob">
                    <div data-id="element-442" data-mp="2de4xcpkjnijd">
                      <label htmlFor="tradeYear" className={labelClass} data-id="element-443" data-mp="2ddd59oq5o2xu">
                        {t('tradein.year')}
                      </label>
                      <input id="tradeYear" type="text" inputMode="numeric" maxLength={4} placeholder={t('tradein.yearPlaceholder')} value={values.year} onChange={e => set('year', e.target.value.replace(/\D/g, ''))} className={`${inputClass} tabular-nums`} data-id="element-444" data-mp="24cyi0jtmsdrp" />
                    </div>
                    <div data-id="element-445" data-mp="ueyoie6kphiu">
                      <label htmlFor="tradeMake" className={labelClass} data-id="element-446" data-mp="ue5so895kcfh">
                        {t('tradein.make')}
                      </label>
                      <input id="tradeMake" type="text" placeholder={t('tradein.makePlaceholder')} value={values.make} onChange={e => set('make', e.target.value)} className={inputClass} data-id="element-447" data-mp="ghs8grd8hwxb" />
                    </div>
                    <div className="col-span-2 sm:col-span-1" data-id="element-448" data-mp="3synq5sl1luiz">
                      <label htmlFor="tradeModel" className={labelClass} data-id="element-449" data-mp="1prp7czaonwj">
                        {t('tradein.model')}
                      </label>
                      <input id="tradeModel" type="text" placeholder={t('tradein.modelPlaceholder')} value={values.model} onChange={e => set('model', e.target.value)} className={inputClass} data-id="element-450" data-mp="3jy91qkcs7zb9" />
                    </div>
                  </div>
                </div>}

              <div data-id="element-451" data-mp="3a8ajva9zgpse">
                <label htmlFor="tradeMiles" className={labelClass} data-id="element-452" data-mp="3a7r4o1bpwfi2">
                  {t('tradein.miles')}
                </label>
                <p id="tradeMiles-help" className={helpClass} data-id="element-453" data-mp="3o68ovp2ez9v8">
                  {t('tradein.milesHelp')}
                </p>
                <input id="tradeMiles" name="tradeMiles" type="text" inputMode="numeric" placeholder={t('tradein.milesPlaceholder')} value={values.miles}
              // Thousands separators are added live: "120000" reads back as "120,000".
              onChange={e => set('miles', formatThousands(e.target.value))} aria-invalid={Boolean(error)} aria-describedby={`tradeMiles-help${error ? ' tradein-error' : ''}`} className={`${inputClass} tabular-nums`} data-id="element-454" data-mp="1sh44xnh7ice" />
                {values.miles ? <p className="mt-1.5 text-xs font-semibold text-camelink" data-id="element-455" data-mp="1r6keruoe1xdu">
                    {t('tradein.milesReadout', {
                  n: values.miles
                })}
                  </p> : null}
              </div>

              <div data-id="element-456" data-mp="yhltwpsvsh4u">
                <label htmlFor="tradePayoff" className={labelClass} data-id="element-457" data-mp="yh3slerd3cq2">
                  {t('tradein.payoff')}
                </label>
                <p id="tradePayoff-help" className={helpClass} data-id="element-458" data-mp="13fndrcy4skhd">
                  {t('tradein.payoffHelp')}
                </p>
                <div className="flex items-center border border-ink/20 bg-white focus-within:border-ink" data-id="element-459" data-mp="189j5hxnsxb43">
                  <span aria-hidden="true" className="pl-4 text-base text-muted" data-id="element-460" data-mp="frdvum41ggvi">
                    $
                  </span>
                  <input id="tradePayoff" name="tradePayoff" type="text" inputMode="numeric" disabled={values.payoffUnknown} placeholder={t('tradein.payoffPlaceholder')} value={values.payoffUnknown ? '' : values.payoff} onChange={e => set('payoff', formatThousands(e.target.value))} aria-describedby="tradePayoff-help" className="min-h-[52px] w-full bg-transparent px-2 text-base tabular-nums text-body placeholder:text-muted/60 focus:outline-none disabled:cursor-not-allowed" data-id="element-461" data-mp="884oeyf3es93" />
                </div>
                <div className="mt-3 flex items-start gap-3" data-id="element-462" data-mp="36qtidcbo268t">
                  <input id="payoffUnknown" type="checkbox" checked={values.payoffUnknown} onChange={e => set('payoffUnknown', e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-ink" data-id="element-463" data-mp="32oex6lkiyued" />
                  <label htmlFor="payoffUnknown" className="text-sm leading-snug text-body" data-id="element-464" data-mp="3dur5hca42q0v">
                    {t('tradein.payoffUnknown')}
                    <span className="mt-0.5 block text-xs text-muted" data-id="element-465" data-mp="3skygj6xsh6rj">
                      {t('tradein.payoffUnknownNote')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {error ? <p id="tradein-error" role="alert" className="mt-4 text-sm text-kred" data-id="element-466" data-mp="2uw6j1xj608tk">
                {error}
              </p> : null}

            <Button type="submit" className="mt-6 sm:px-10" data-id="element-467" data-mp="39q1d3xenvtkp">
              {t('tradein.cta')}
            </Button>
            <p className="mt-3 text-xs text-muted" data-id="element-468" data-mp="p259lczing6c">{t('tradein.note')}</p>
          </form>
        </Reveal>

        <Reveal delay={0.15} className="mt-12" data-id="element-469" data-mp="k3c1k0300hdk">
          <PaymentCalculator data-id="element-470" data-mp="k2k5ko5hohmr" />
        </Reveal>
      </div>
    </section>;
}